/**
 * ShippingOptions - Child Web Component
 *
 * Handles postcode-based shipping rate calculations.
 * This component is a child of DeliveryOptions and focuses only on:
 * - Form handling for postcode input
 * - Fetching shipping rates from Shopify API
 * - Rendering shipping rate results
 * - Address caching in localStorage
 * - AusPost Same Day eligibility
 *
 * Communicates with parent via custom events:
 * - 'status-change': Emits status updates for row display
 */

import { escapeHtml } from '@theme/globale-shipping-shared';
import { Translator } from './translator.js';

class ShippingOptions extends HTMLElement {

  // Storage keys
  static STORAGE_ADDRESS_KEY = 'amb.shipping_address';

  // View states
  VIEW_FORM = 'form';
  VIEW_LOADING = 'loading';
  VIEW_RESULT = 'result';

  // Excluded shipping methods (by name)
  EXCLUDED_SHIPPING_NAMES = ['PICK_UP', 'Local Delivery'];

  // Data from parent
  data = {};

  // Translator instance
  translator = null;

  // Component state
  state = {
    view: 'form',
    loading: false,
    error: null,
    address: { country: '', province: '', zip: '' },
    rates: [],
    availability: null // Stores availability data from API response
  };

  // Cached data
  savedAddress = null;
  savedAddresses = [];
  savedResults = [];
  zipCodes = {};

  // DOM References
  formView = null;
  loadingView = null;
  resultsView = null;
  form = null;
  countrySelect = null;
  provinceSelect = null;
  zipInput = null;
  searchButton = null;
  clearButton = null;
  resultsContainer = null;

  // Event handlers for cleanup
  submitHandler = null;
  zipInputHandler = null;
  clearHandler = null;
  searchHandler = null;

  // Initialization flag
  initialized = false;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.queryElements();
    this.registerEvents();
  }

  disconnectedCallback() {
    this.removeEvents();
    this.initialized = false;
  }

  // ============================================
  // INITIALIZATION (called by parent)
  // ============================================

  /**
   * Initialize component with data from parent
   * @param {Object} data - Configuration data
   */
  init(data) {
    this.data = data;

    // Initialize Translator (no namespace - access keys with full paths)
    this.translator = new Translator('delivery-translations');

    this.loadSavedAddress();
    this.initCountryProvince();
    this.restoreSavedAddress();
    this.autoFetchIfCached();
  }

  /**
   * Handle variant change from parent
   * @param {Object} data - Variant data
   */
  handleVariantChange(data) {
    this.data = { ...this.data, ...data };
    this.savedResults = []; // Clear cached results
    this.state.availability = null; // Clear availability data

    // Re-fetch if we have a saved address - rates may differ for new variant
    if (this.savedAddress?.zip) {
      this.fetchShippingRates();
    } else {
      // No saved address - reset to form view
      this.setState({ view: this.VIEW_FORM, rates: [], error: null });
      this.showView(this.VIEW_FORM);
      this.emitStatusChange('normal');
    }
  }

  /**
   * Handle cart change from parent
   * @param {Object} data - Cart data
   */
  handleCartChange(data) {
    this.data = data;
    this.savedResults = []; // Clear cached results
    this.state.availability = null; // Clear availability data

    // Re-query DOM elements (they may have been replaced)
    this.queryElements();

    // Restore saved address to form (input may have been re-rendered)
    this.restoreSavedAddress();

    // Re-fetch rates with current saved address
    this.autoFetchIfCached();
  }

  // ============================================
  // DOM QUERIES
  // ============================================

  /**
   * Query and store references to DOM elements
   */
  queryElements() {
    // Views
    this.formView = this.querySelector('[ref="form-view"]');
    this.loadingView = this.querySelector('[ref="loading-view"]');
    this.resultsView = this.querySelector('[ref="results-view"]');

    // Form elements
    this.form = this.querySelector('[ref="form"]');
    this.countryContainer = this.querySelector('[ref="country-container"]');
    this.countrySelect = this.querySelector('[ref="country-select"]');
    this.provinceContainer = this.querySelector('[ref="province-container"]');
    this.provinceSelect = this.querySelector('[ref="province-select"]');
    this.zipInput = this.querySelector('[ref="zip-input"]');
    this.zipError = this.querySelector('[ref="zip-error"]');
    this.searchButton = this.querySelector('[ref="search-button"]');
    this.clearButton = this.querySelector('[ref="clear-button"]');
    this.formMessages = this.querySelector('[ref="form-messages"]');

    // Results
    this.resultsContainer = this.querySelector('[ref="results-container"]');
  }

  // ============================================
  // EVENT REGISTRATION
  // ============================================

  /**
   * Register form event listeners
   */
  registerEvents() {
    if (!this.form) return;

    // Form submit
    this.submitHandler = this.handleSubmit.bind(this);
    this.form.addEventListener('submit', this.submitHandler);

    // Zip input changes
    if (this.zipInput) {
      this.zipInputHandler = this.handleZipInput.bind(this);
      this.zipInput.addEventListener('input', this.zipInputHandler);
    }

    // Clear button
    if (this.clearButton) {
      this.clearHandler = this.handleClear.bind(this);
      this.clearButton.addEventListener('click', this.clearHandler);
    }

    // Search button
    if (this.searchButton) {
      this.searchHandler = (e) => {
        e.preventDefault();
        this.form.requestSubmit();
      };
      this.searchButton.addEventListener('click', this.searchHandler);
    }
  }

  /**
   * Remove all event listeners
   */
  removeEvents() {
    if (this.form && this.submitHandler) {
      this.form.removeEventListener('submit', this.submitHandler);
    }
    if (this.zipInput && this.zipInputHandler) {
      this.zipInput.removeEventListener('input', this.zipInputHandler);
    }
    if (this.clearButton && this.clearHandler) {
      this.clearButton.removeEventListener('click', this.clearHandler);
    }
    if (this.searchButton && this.searchHandler) {
      this.searchButton.removeEventListener('click', this.searchHandler);
    }
  }

  // ============================================
  // FORM HANDLERS
  // ============================================

  /**
   * Handle form submission
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();
    await this.fetchShippingRates();
  }

  /**
   * Handle zip input changes
   */
  handleZipInput() {
    const hasValue = !!this.zipInput?.value;

    if (this.clearButton) {
      this.clearButton.hidden = !hasValue;
    }
    if (this.searchButton) {
      this.searchButton.hidden = !hasValue;
    }

    // Hide any previous errors
    this.hideFieldError(this.zipError);
    this.hideFormMessages();
  }

  /**
   * Handle clear button click
   */
  handleClear() {
    if (this.zipInput) {
      this.zipInput.value = '';
      this.zipInput.focus();
    }
    if (this.clearButton) this.clearButton.hidden = true;
    if (this.searchButton) this.searchButton.hidden = true;

    this.hideFieldError(this.zipError);
    this.hideFormMessages();
    this.state.availability = null; // Clear availability data
    this.showView(this.VIEW_FORM);
    this.emitStatusChange('normal');
  }

  // ============================================
  // COUNTRY/PROVINCE HANDLING
  // ============================================

  /**
   * Initialize country and province selectors
   */
  initCountryProvince() {
    const hasZipcodes = this.data.assets?.zipcodes;

    // Hide country/province for countries with zipcode validation
    if (hasZipcodes || hasZipcodes === false) {
      if (this.countryContainer) {
        this.countryContainer.classList.add('hidden');
      }
      if (this.provinceContainer) {
        this.provinceContainer.style.display = 'none';
      }
    }
  }

  // ============================================
  // ADDRESS MANAGEMENT
  // ============================================

  /**
   * Load saved address from localStorage
   */
  loadSavedAddress() {
    try {
      const stored = localStorage.getItem(ShippingOptions.STORAGE_ADDRESS_KEY);
      if (stored) {
        this.savedAddresses = JSON.parse(stored);
        if (!Array.isArray(this.savedAddresses)) {
          this.savedAddresses = this.savedAddresses ? [this.savedAddresses] : [];
        }
        this.savedAddress = this.savedAddresses.find(
          addr => addr.country === this.data.defaultCountry
        ) || null;
      }
    } catch (error) {
      console.error('ShippingOptions: Error loading saved address', error);
      this.savedAddresses = [];
      this.savedAddress = null;
    }
  }

  /**
   * Restore saved address to form fields
   */
  restoreSavedAddress() {
    if (!this.savedAddress) return;

    if (this.zipInput && this.savedAddress.zip) {
      this.zipInput.value = this.savedAddress.zip;
      this.handleZipInput();
    }

    if (this.countrySelect && this.savedAddress.country) {
      this.countrySelect.value = this.savedAddress.country;
    }

    if (this.provinceSelect && this.savedAddress.province) {
      this.provinceSelect.value = this.savedAddress.province;
    }
  }

  /**
   * Save address to localStorage
   * @param {Object} address
   */
  saveAddress(address) {
    this.savedAddress = address;

    const existingIndex = this.savedAddresses.findIndex(
      a => a.country === address.country
    );

    if (existingIndex === -1) {
      this.savedAddresses.push(address);
    } else {
      this.savedAddresses[existingIndex] = address;
    }

    try {
      localStorage.setItem(
        ShippingOptions.STORAGE_ADDRESS_KEY,
        JSON.stringify(this.savedAddresses)
      );
    } catch (error) {
      console.error('ShippingOptions: Error saving address', error);
    }
  }

  /**
   * Auto-fetch if cached address exists
   */
  autoFetchIfCached() {
    if (!this.data.requiresShipping) {
      return;
    }

    if (this.savedAddress?.zip) {
      this.fetchShippingRates();
    } else {
      this.emitStatusChange('normal');
    }
  }

  // ============================================
  // SHIPPING RATES API
  // ============================================

  /**
   * Fetch shipping rates from Shopify API
   */
  async fetchShippingRates() {
    // Check if product/cart requires shipping
    if (!this.data.requiresShipping) {
      this.showFormError(this.t('delivery.shipping.not_required'));
      this.emitStatusChange('normal');
      return;
    }

    // Check availability (PDP context)
    if (this.data.context === 'pdp' && !this.data.available) {
      this.showFormError(this.t('delivery.shipping.unavailable'));
      this.emitStatusChange('unavailable', '', this.t('delivery.shipping.unavailable'));
      return;
    }

    // Check cart not empty (Cart context)
    if (this.data.context === 'cart' && (!this.data.lineItems || this.data.lineItems.length === 0)) {
      this.showFormError(this.t('delivery.shipping.cart_empty'));
      this.emitStatusChange('normal');
      return;
    }

    const zip = this.zipInput?.value?.trim();
    if (!zip) {
      this.showFieldError(this.zipError, this.t('delivery.shipping.zip_required'));
      return;
    }

    // Show loading state
    this.showView(this.VIEW_LOADING);
    this.emitStatusChange('loading');

    try {
      // Validate zipcode and get province
      const province = await this.validateZipcode(zip);
      if (province === false) {
        // Invalid zipcode
        this.showView(this.VIEW_FORM);
        this.showFieldError(this.zipError, this.t('delivery.shipping.invalid_zip'));
        this.emitStatusChange('unavailable');
        return;
      }

      // Build address
      const address = {
        country: this.data.defaultCountry,
        province: province || '',
        zip: zip
      };

      // Check cache
      const cached = this.getCachedRates(address);
      if (cached) {
        // Restore availability data from cache
        this.state.availability = cached.availability || null;
        this.handleRatesResult(cached.rates, address, zip);
        return;
      }

      // Check if this is AU or NZ address
      const isAUorNZ = ['AU', 'Australia', 'NZ', 'New Zealand'].includes(address.country);

      // Fetch rates (uses appropriate API based on country)
      const { rates, errors } = await this.fetchRatesFromAPI(address);

      if (errors) {
        this.showView(this.VIEW_FORM);
        this.showFormError(Object.values(errors).flat().join(' '));
        this.emitStatusChange('unavailable', zip);
        return;
      }

      // Filter and process rates
      let shippingRates = this.filterRates(rates || []);

      // Add AusPost Same Day if eligible
      if (this.isAusPostSameDayEligible(zip)) {
        shippingRates.push(this.getAusPostSameDayOption(zip));
      }

      // Cache and display
      this.cacheRates(address, shippingRates, this.state.availability);
      this.handleRatesResult(shippingRates, address, zip);

    } catch (error) {
      console.error('ShippingOptions: Error fetching rates', error);
      this.showView(this.VIEW_FORM);
      this.showFormError(this.t('delivery.shipping.fetch_error'));
      this.emitStatusChange('unavailable', zip, this.t('delivery.shipping.fetch_error'));
    }
  }

  /**
   * Handle rates result (from API or cache)
   * @param {Array} rates
   * @param {Object} address
   * @param {string} zip
   */
  handleRatesResult(rates, address, zip) {
    if (rates.length > 0) {
      this.saveAddress(address);
      this.renderRates(rates);
      this.showView(this.VIEW_RESULT);
      
      const availabilityData = this.state.availability;
      const isPdp = this.data.context === 'pdp';
      
      // For PDP context: check availability data to determine status
      if (isPdp && availabilityData && availabilityData.summary) {
        const { available, unavailable, total } = availabilityData.summary;
        
        if (unavailable > 0) {
          // Some or all items unavailable
          const statusText = this.t('delivery.shipping.pdp_not_available', {}, 'Not available');
          this.emitStatusChange('unavailable', zip, null, statusText);
          return;
        } else if (available === total && total > 0) {
          // All items available
          const statusText = this.t('delivery.shipping.pdp_available', {}, 'Available');
          this.emitStatusChange('available', zip, null, statusText);
          return;
        }
      }
      
      // For cart context: check availability data
      if (!isPdp && availabilityData && availabilityData.summary) {
        const { available, unavailable, total, partiallyAvailable } = availabilityData.summary;
        
        // Check for unavailable items first
        if (unavailable > 0 && available === 0) {
          // All items unavailable
          const statusText = this.t('delivery.shipping.cart_not_available', {}, 'No items available');
          this.emitStatusChange('unavailable', zip, null, statusText);
          return;
        } else if (unavailable > 0 && available > 0) {
          // Some items unavailable (partial availability)
          const context = {
            availability: {
              available: available,
              all: total,
              unavailable: unavailable
            }
          };
          const statusText = this.t('delivery.shipping.cart_partially_available', context, `${available} of ${total} items available`);
          this.emitStatusChange('available', zip, null, statusText);
          return;
        } else if (partiallyAvailable > 0) {
          // Partial availability (legacy check)
          const context = {
            availability: {
              available: available,
              all: total,
              unavailable: total - available
            }
          };
          const statusText = this.t('delivery.shipping.cart_partially_available', context, `${available} of ${total} items available`);
          this.emitStatusChange('available', zip, null, statusText);
          return;
        } else if (available === total && total > 0) {
          // All items available
          const statusText = this.t('delivery.shipping.cart_available', {}, 'All items available');
          this.emitStatusChange('available', zip, null, statusText);
          return;
        }
      }
      
      // Default: available (for cases without availability data)
      const statusText = isPdp 
        ? this.t('delivery.shipping.pdp_available', {}, 'Available')
        : this.t('delivery.shipping.cart_available', {}, 'All items available');
      this.emitStatusChange('available', zip, null, statusText);
    } else {
      this.showView(this.VIEW_FORM);
      this.showFormError(this.t('delivery.shipping.no_result'));
      this.emitStatusChange('unavailable', zip);
    }
  }

  /**
   * Validate zipcode and return province if valid
   * @param {string} zip
   * @returns {Promise<string|false>}
   */
  async validateZipcode(zip) {
    const zipcodeUrl = this.data.assets?.zipcodes;

    if (!zipcodeUrl) {
      // No zipcode validation for this country
      return '';
    }

    // Load zipcodes if not cached
    const country = this.data.countryIsoCode;
    if (!this.zipCodes[country]) {
      try {
        const response = await fetch(zipcodeUrl);
        this.zipCodes[country] = await response.json();
      } catch (error) {
        console.error('ShippingOptions: Error loading zipcodes', error);
        return '';
      }
    }

    // Find province for zipcode
    const provinces = this.zipCodes[country];
    for (const [provinceName, postcodes] of Object.entries(provinces)) {
      if (postcodes.includes(zip)) {
        return provinceName;
      }
    }

    return false;
  }

  /**
   * Fetch rates from Shopify API
   * For AU/NZ addresses: Uses Hydrogen server API to get accurate rates with shipping discounts
   * For other countries: Uses Global-e shipping details API
   * @param {Object} address
   * @returns {Promise<{rates: Array, errors: Object|null}>}
   */
  async fetchRatesFromAPI(address) {
    // Check if this is AU or NZ address
    const isAUorNZ = ['AU', 'Australia', 'NZ', 'New Zealand'].includes(address.country);
    
    // For AU/NZ: Use Hydrogen server API with GraphQL Cart API
    // For PDP context: pass the variant to include in the request
    if (isAUorNZ) {
      const pdpVariantForAUNZ = (this.data.context === 'pdp') ? this.data.variantId : null;
      return this.fetchAUNZRatesFromHydrogen(address, pdpVariantForAUNZ);
    }
    
    // For other countries: Global-e handles shipping
    // Return empty rates as Global-e shipping details are shown separately
    return { rates: [], errors: null };
  }

  /**
   * Fetch AU/NZ shipping rates from Hydrogen server
   * Uses GraphQL Cart API to get accurate rates with shipping discounts
   * @param {Object} address
   * @param {number|null} pdpVariantId - Optional variant ID for PDP context
   * @returns {Promise<{rates: Array, errors: Object|null}>}
   */
  async fetchAUNZRatesFromHydrogen(address, pdpVariantId = null) {
    try {
      // Get current cart state
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Build line items from cart
      let lineItems = cart.items.map(item => ({
        variantId: item.variant_id,
        quantity: item.quantity
      }));
      
      // For PDP context with empty cart, include the PDP variant
      if (pdpVariantId) {
        lineItems = [{ variantId: pdpVariantId, quantity: 1 }];
      } else if (lineItems.length === 0) {
        return { 
          rates: null, 
          errors: { result: [this.t('delivery.shipping.cart_empty')] } 
        };
      }
      
      // Call Hydrogen server API
      const hydrogenGetShippingRatesUrl = '/apps/api/get-shipping-rates';
      
      const response = await fetch(hydrogenGetShippingRatesUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lineItems,
          address: {
            country: address.country,
            province: address.province,
            zip: address.zip
          },
          locationId: this.data.locationId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('ShippingOptions: Hydrogen API error', errorData);
        return { 
          rates: null, 
          errors: errorData.userErrors || { result: [errorData.error || 'Failed to fetch shipping rates'] } 
        };
      }
      
      const data = await response.json();
      
      if (data && 'shipping_rates' in data) {
        // Store availability data in component state
        if (data.availability) {
          this.state.availability = data.availability;
        }
        return { rates: data.shipping_rates, errors: null };
      }
      
      return { rates: null, errors: { result: ['No shipping rates returned'] } };
      
    } catch (error) {
      console.error('ShippingOptions: Hydrogen API error', error);
      return { 
        rates: null, 
        errors: { result: [this.t('delivery.shipping.fetch_error')] } 
      };
    }
  }

  /**
   * Filter out excluded shipping methods
   * @param {Array} rates
   * @returns {Array}
   */
  filterRates(rates) {
    return rates.filter(rate => {
      // Filter by name (e.g., "Local Delivery", "PICK_UP")
      if (rate.name && this.EXCLUDED_SHIPPING_NAMES.includes(rate.name)) {
        return false;
      }
      return true;
    }).map(({ name, presentment_name, price, delivery_days, currency, description, code, handle, original_price, discount }) => ({
      name, 
      presentment_name, 
      price, 
      delivery_days, 
      currency, 
      description, 
      code,
      handle,
      ...(original_price && { original_price }),
      ...(discount && { discount })
    }));
  }

  /**
   * Add item to cart temporarily (for PDP rate calculation)
   * @param {number} variantId
   * @returns {Promise<Object|null>}
   */
  async addToCart(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('ShippingOptions: Error adding to cart', error);
    }
    return null;
  }

  /**
   * Remove item from cart
   * @param {Object} lineItem
   * @returns {Promise<void>}
   */
  async removeFromCart(lineItem) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineItem.key, quantity: 0 })
      });
    } catch (error) {
      console.error('ShippingOptions: Error removing from cart', error);
    }
  }

  // ============================================
  // AUSPOST SAME DAY
  // ============================================

  /**
   * Check if postcode is eligible for AusPost Same Day
   * @param {string} postcode
   * @returns {boolean}
   */
  isAusPostSameDayEligible(postcode) {
    const priceData = this.data.auspostPriceData;
    if (!priceData || !postcode) return false;

    let postcodes = priceData.postcodes;
    if (postcodes?.value) postcodes = postcodes.value;
    if (!Array.isArray(postcodes)) return false;

    const postcodeInt = parseInt(postcode, 10);
    return postcodes.some(pc => parseInt(pc, 10) === postcodeInt);
  }

  /**
   * Get AusPost Same Day option
   * @param {string} postcode
   * @returns {Object}
   */
  getAusPostSameDayOption(postcode) {
    let price = this.data.auspostPriceData?.price;
    if (price?.value !== undefined) price = price.value;

    return {
      name: 'AusPost Same Day',
      presentment_name: this.t('delivery.shipping.auspost_same_day'),
      price: price ? String(price) : '0',
      delivery_days: null,
      description: this.t('delivery.shipping.auspost_same_day_description')
    };
  }

  // ============================================
  // CACHING
  // ============================================

  /**
   * Get address key for caching
   * @param {Object} address
   * @returns {string}
   */
  getAddressKey(address) {
    return `${address.country}|${address.province}|${address.zip}`;
  }

  /**
   * Get line items key for caching
   * @returns {string}
   */
  getLineItemsKey() {
    const items = (this.data.lineItems || []).map((id, i) =>
      `${id}-${this.data.quantities?.[i] || 1}`
    );

    if (this.data.context === 'pdp' && !this.data.lineItems?.includes(this.data.variantId)) {
      items.push(`${this.data.variantId}-1`);
    }

    return items.sort().join('|');
  }

  /**
   * Get cached rates
   * @param {Object} address
   * @returns {Array|null}
   */
  getCachedRates(address) {
    const addressKey = this.getAddressKey(address);
    const lineItemsKey = this.getLineItemsKey();

    const cached = this.savedResults.find(
      r => r.address === addressKey && r.lineItems === lineItemsKey
    );

    if (!cached) return null;

    return {
      rates: cached.rates || null,
      availability: cached.availability || null
    };
  }

  /**
   * Cache rates and availability data
   * @param {Object} address
   * @param {Array} rates
   * @param {Object|null} availability - Availability data from API
   */
  cacheRates(address, rates, availability = null) {
    const addressKey = this.getAddressKey(address);
    const lineItemsKey = this.getLineItemsKey();

    const existingIndex = this.savedResults.findIndex(
      r => r.address === addressKey && r.lineItems === lineItemsKey
    );

    const cacheEntry = {
      address: addressKey,
      lineItems: lineItemsKey,
      timestamp: Date.now(),
      rates,
      availability
    };

    if (existingIndex === -1) {
      this.savedResults.unshift(cacheEntry);
    } else {
      this.savedResults.splice(existingIndex, 1);
      this.savedResults.unshift(cacheEntry);
    }
  }

  // ============================================
  // VIEW MANAGEMENT
  // ============================================

  /**
   * Show specific view
   * @param {'form' | 'loading' | 'result'} viewName
   */
  showView(viewName) {
    this.state.view = viewName;

    if (this.loadingView) {
      this.loadingView.classList.toggle('hidden', viewName !== this.VIEW_LOADING);
    }
    if (this.resultsView) {
      this.resultsView.classList.toggle('hidden', viewName !== this.VIEW_RESULT);
    }
  }

  /**
   * Update component state
   * @param {Object} updates
   */
  setState(updates) {
    this.state = { ...this.state, ...updates };
  }

  // ============================================
  // RENDERING
  // ============================================

  /**
   * Render shipping rates
   * @param {Array} rates
   */
  renderRates(rates) {
    if (!this.resultsContainer) return;

    const fragment = document.createDocumentFragment();

    // Determine availability status from state (match logic from handleRatesResult)
    const availabilityData = this.state.availability;
    const isPdp = this.data.context === 'pdp';
    let availabilityStatus = 'available'; // default
    
    if (availabilityData && availabilityData.summary) {
      const { available, unavailable, total, partiallyAvailable } = availabilityData.summary;
      
      // For PDP context
      if (isPdp) {
        if (unavailable > 0) {
          availabilityStatus = 'unavailable';
        } else if (available === total && total > 0) {
          availabilityStatus = 'available';
        }
      } else {
        // For cart context
        if (unavailable > 0 && available === 0) {
          // All items unavailable
          availabilityStatus = 'unavailable';
        } else if (available === total && total > 0) {
          // All items available
          availabilityStatus = 'available';
        }
      }
    }

    rates.forEach(rate => {
      const item = this.getTemplate('rate-item');
      if (!item) return;

      const container = item.querySelector('.shipping-options__rate') || item;

      const titleEl = container.querySelector('.shipping-options__rate-title');
      const descEl = container.querySelector('.shipping-options__rate-description');
      const costEl = container.querySelector('.shipping-options__rate-cost');
      const iconEl = container.querySelector('.shipping-options__rate-icon');

      if (titleEl) {
        titleEl.textContent = rate.presentment_name || rate.name;
      }

      if (descEl) {
        descEl.textContent = this.formatDeliveryDays(rate.delivery_days, rate.description);
      }

      if (costEl) {
        costEl.innerHTML = this.formatPrice(rate.price, rate.original_price, rate.discount);
      }

      // Update icon status based on availability
      if (iconEl) {
        iconEl.setAttribute('data-status', availabilityStatus);
      }

      fragment.appendChild(item);
    });

    this.resultsContainer.innerHTML = '';
    this.resultsContainer.appendChild(fragment);
  }


  /**
   * Format price for display
   * @param {string|number} price - Final price after discounts
   * @param {string|number} originalPrice - Original price before discounts (optional)
   * @param {Object} discount - Discount information (optional)
   * @returns {string}
   */
  formatPrice(price, originalPrice = null, discount = null) {
    const priceValue = parseFloat(price) || 0;

    if (priceValue === 0) {
      return this.t('delivery.shipping.free');
    }

    const symbol = escapeHtml(this.data.currencySymbol || '$');
    const currency = escapeHtml(this.data.currency || 'AUD');

    // If there's a discount, show both original and discounted price
    if (originalPrice && discount && parseFloat(originalPrice) > priceValue) {
      const originalFormatted = `${symbol}${parseFloat(originalPrice).toFixed(2)}`;
      const finalFormatted = `${symbol}${priceValue.toFixed(2)}`;
      
      return `<span class="shipping-price-original" style="text-decoration: line-through; opacity: 0.6;">${originalFormatted}</span> ${finalFormatted} <span class="currency-symbol">${currency}</span>`;
    }

    return `${symbol}${priceValue.toFixed(2)} <span class="currency-symbol">${currency}</span>`;
  }

  /**
   * Format delivery days
   * @param {Array} deliveryDays
   * @param {string} description
   * @returns {string}
   */
  formatDeliveryDays(deliveryDays, description) {
    if (Array.isArray(deliveryDays) && deliveryDays.length === 2) {
      const [min, max] = deliveryDays;
      if (!isNaN(parseInt(min)) && !isNaN(parseInt(max))) {
        return this.t('delivery.shipping.shipping_days', { min, max });
      }
    }
    return description || '';
  }

  // ============================================
  // ERROR DISPLAY
  // ============================================

  /**
   * Show field-specific error
   * @param {HTMLElement} errorEl
   * @param {string} message
   */
  showFieldError(errorEl, message) {
    if (!errorEl) return;
    const msgEl = errorEl.querySelector('.feedback-message');
    if (msgEl) msgEl.textContent = message;
    errorEl.classList.remove('hidden');
  }

  /**
   * Hide field error
   * @param {HTMLElement} errorEl
   */
  hideFieldError(errorEl) {
    if (errorEl) {
      errorEl.classList.add('hidden');
    }
  }

  /**
   * Show form-level error
   * @param {string} message
   */
  showFormError(message) {
    if (!this.formMessages) return;

    const errorEl = this.getTemplate('error-message');
    if (!errorEl) return;

    const msgEl = errorEl.querySelector('.shipping-options__message-text') ||
                  errorEl.querySelector('.feedback-message');
    if (msgEl) msgEl.textContent = message;

    this.formMessages.innerHTML = '';
    this.formMessages.appendChild(errorEl);
    this.formMessages.classList.remove('hidden');
  }

  /**
   * Hide form messages
   */
  hideFormMessages() {
    if (this.formMessages) {
      this.formMessages.classList.add('hidden');
      this.formMessages.innerHTML = '';
    }
  }

  // ============================================
  // PARENT COMMUNICATION
  // ============================================

  /**
   * Emit status change to parent
   * @param {'loading' | 'normal' | 'available' | 'unavailable'} status
   * @param {string} label
   * @param {string|null} error
   * @param {string} statusText - Custom status text to display
   */
  emitStatusChange(status, label = '', error = null, statusText = '') {
    // Get default status text if not provided
    const displayText = statusText || this.getDefaultStatusText(status);

    this.dispatchEvent(new CustomEvent('status-change', {
      bubbles: true,
      detail: { status, label, error, statusText: displayText }
    }));

    // Update cart line items with shipping availability (cart context only)
    if (this.data.context === 'cart') {
      if (status === 'available') {
        this.updateCartLineItemsAvailability('available', this.t('delivery.shipping.line_item_available', {}, 'Delivery available'));
      } else if (status === 'unavailable') {
        this.updateCartLineItemsAvailability('unavailable', this.t('delivery.shipping.line_item_not_available', {}, 'Delivery not available'));
      } else if (status === 'loading') {
        this.updateCartLineItemsAvailability('loading', this.t('accessibility.loading', {}, 'Loading...'));
      } else {
        // 'normal' status - hide the availability indicators
        this.hideCartLineItemsAvailability();
      }
    }
  }

  /**
   * Get default status text for a given status
   * Uses context-appropriate messages (pdp vs cart)
   * @param {string} status
   * @returns {string}
   */
  getDefaultStatusText(status) {
    const isPdp = this.data.context === 'pdp';

    switch (status) {
      case 'loading':
        return this.t('accessibility.loading', {}, 'Loading...');
      case 'available':
        return isPdp
          ? this.t('delivery.shipping.pdp_available', {}, 'Available')
          : this.t('delivery.shipping.cart_available', {}, 'All items available');
      case 'unavailable':
        return isPdp
          ? this.t('delivery.shipping.pdp_not_available', {}, 'Not available')
          : this.t('delivery.shipping.cart_not_available', {}, 'No items available');
      case 'normal':
      default:
        return this.t('delivery.shipping.check_availability', {}, 'Check availability');
    }
  }

  /**
   * Normalize variant ID to numeric string for comparison
   * Handles both GID format (gid://shopify/ProductVariant/123456) and numeric IDs
   * @param {string|number} variantId
   * @returns {string} - Always returns numeric string for consistent comparison
   */
  normalizeVariantId(variantId) {
    if (!variantId) return '';
    
    // Convert to string first
    const idStr = String(variantId);
    
    // If it's a GID format, extract the numeric part
    if (idStr.startsWith('gid://')) {
      const match = idStr.match(/\/ProductVariant\/(\d+)$/);
      return match ? match[1] : idStr;
    }
    
    // Otherwise, return as string (handles numeric IDs)
    return idStr;
  }

  /**
   * Update cart line items with individual shipping availability status
   * Uses availability data from API response to set status per line item
   *
   * @param {'available' | 'unavailable' | 'loading'} status - Overall status (used if no per-item data)
   * @param {string} text - Default status text to display
   */
  updateCartLineItemsAvailability(status, text) {
    if (this.data.context !== 'cart') return;

    const elements = document.querySelectorAll('.shipping-availability');
    const availabilityData = this.state.availability;

    // If we have per-item availability data, use it
    if (availabilityData && availabilityData.lineItems && Array.isArray(availabilityData.lineItems)) {
      const availabilityMap = new Map();
      
      // Create a map of variantId -> available status
      availabilityData.lineItems.forEach(item => {
        const normalizedId = this.normalizeVariantId(item.variantId);
        availabilityMap.set(normalizedId, item.available);
      });

      elements.forEach(element => {
        const variantIdAttr = element.getAttribute('data-variant-id');
        if (!variantIdAttr) return;

        // Normalize the variant ID from HTML attribute for comparison
        const normalizedAttrId = this.normalizeVariantId(variantIdAttr);
        
        // Get availability for this specific variant
        const isAvailable = availabilityMap.get(normalizedAttrId);
        
        // Determine status and text for this item
        let itemStatus, itemText;
        if (isAvailable === true) {
          itemStatus = 'available';
          itemText = this.t('delivery.shipping.line_item_available', {}, 'Delivery available');
        } else if (isAvailable === false) {
          itemStatus = 'unavailable';
          itemText = this.t('delivery.shipping.line_item_not_available', {}, 'Delivery not available');
        } else {
          // Fallback to overall status if variant not found in availability data
          itemStatus = status;
          itemText = text;
        }

        // Show the element
        element.classList.remove('hidden');

        // Update indicator status
        const indicator = element.querySelector('.availability-indicator');
        if (indicator) {
          indicator.className = 'availability-indicator';
          indicator.classList.add(`availability-indicator--${itemStatus}`);
        }

        // Update text
        const textElement = element.querySelector('.availability-text');
        if (textElement) {
          textElement.textContent = itemText;
        }
      });
    } else {
      // Fallback: Set all items to the same status (original behavior)
      elements.forEach(element => {
        // Show the element
        element.classList.remove('hidden');

        // Update indicator status
        const indicator = element.querySelector('.availability-indicator');
        if (indicator) {
          indicator.className = 'availability-indicator';
          indicator.classList.add(`availability-indicator--${status}`);
        }

        // Update text
        const textElement = element.querySelector('.availability-text');
        if (textElement) {
          textElement.textContent = text;
        }
      });
    }
  }

  /**
   * Hide all cart line item shipping availability indicators
   */
  hideCartLineItemsAvailability() {
    if (this.data.context !== 'cart') return;

    const elements = document.querySelectorAll('.shipping-availability');
    elements.forEach(element => element.classList.add('hidden'));
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Get template by name from Liquid
   * @param {string} name - Template name attribute
   * @returns {DocumentFragment|null}
   */
  getTemplate(name) {
    const template = this.querySelector(`template[name="${name}"]`);
    if (!template) {
      console.warn(`ShippingOptions: template[name="${name}"] not found`);
      return null;
    }
    return template.content.cloneNode(true);
  }

  /**
   * Get translation using Translator service
   * @param {string} key - Translation key (full path)
   * @param {Object} variables - Optional variables for interpolation
   * @param {string} defaultValue - Optional default value if translation not found
   * @returns {string}
   */
  t(key, variables = {}, defaultValue = undefined) {
    if (this.translator) {
      return this.translator.translate(key, variables, defaultValue);
    }
    return defaultValue !== undefined ? defaultValue : key;
  }
}

// Define the custom element
if (!customElements.get('shipping-options')) {
  customElements.define('shipping-options', ShippingOptions);
}

export { ShippingOptions };
